import React from "react";
import { ScrollView, StyleSheet, TextInput, View } from "react-native";
import { useSelector } from "react-redux";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import api from "src/util/apiNew";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { tCreateConnectionRequestArgs, tCreateConnectionRequestParams } from "tmt_api/src/types/types.validator";
import { AxiosResponse } from "axios";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useIsMobile } from "../ui";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { isApiError } from "src/util/apiNew";
import { useToast } from "../toast";

type tConnectionRequestModal = {
  brandId: string;
  brandName: string;
};

type tCreateConnectionRequestProps = tCreateConnectionRequestParams & { note: string };

const DEFAULT_NOTE_MESSAGE = `Hi there,
I'd love to connect and carry this brand in my shop! Feel free to check my Retailer profile for more information on my store as well as take a look at our website and social media pages.
Look forward to hearing from you!
Best,
(name)`;

const postConnection = ({ brandId, boutiqueId, note }: tCreateConnectionRequestProps) =>
  api
    .post<tCreateConnectionRequestArgs, AxiosResponse<void>>(`/brands/${brandId}/connections/${boutiqueId}/set`, {
      note,
      primaryContact: "",
    })
    .then((res) => res.data);

const ConnectionRequestModal: React.FC<tModalComponentProps<tConnectionRequestModal>> = ({
  brandId,
  brandName,
  onClose,
  onResolve,
}) => {
  const rIsMobile = useIsMobile();
  const rCompany = useSelector(({ CompanyReducer }) =>
    CompanyReducer.type === "retailer" ? CompanyReducer : { type: "retailer", id: "", name: "", vendorId: undefined },
  );
  const toast = useToast();
  const [note, setNote] = React.useState(DEFAULT_NOTE_MESSAGE.replace("(name)", rCompany.name));
  const [isSending, setSending] = React.useState(false);

  const isValid = !!note;

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSend = async () => {
    try {
      setSending(true);
      await postConnection({ brandId, boutiqueId: rCompany.id, note });
      onResolve();
    } catch (error) {
      setSending(false);
      toast({
        description:
          isApiError(error) && error.response ? error.response?.data.message : "An error occurred. Please try again.",
        status: "error",
      });
    }
  };

  return (
    <PdModal closeOnOverlayClick={!isSending} onClose={handleOnClose}>
      <PdModalCloseButton isDisabled={isSending} onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modal} showsHorizontalScrollIndicator={false}>
        <PdModalTitle style={styles.title}>{`Request connection to ${brandName}`}</PdModalTitle>
        <PdText>
          To purchase from this brand, please send a connection request. You may also add their products to your cart
          now, and once they approve your connection request, you can then check out.
        </PdText>
        <View style={styles.mt3}>
          <TextInput
            style={styles.noteTextInput}
            multiline
            numberOfLines={8}
            value={note}
            onChangeText={setNote}
            placeholder="Note"
          />
        </View>
        <PdStack
          isReversed={rIsMobile}
          direction={rIsMobile ? "vertical" : "horizontal"}
          layout={[1, 1]}
          gap={3}
          style={styles.buttonGroup}
        >
          <PdButton colorScheme="purple" isDisabled={isSending} onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton isDisabled={!isValid || isSending} onPress={handleOnSend}>
            <PdButtonLabel>Send</PdButtonLabel>
          </PdButton>
        </PdStack>
      </ScrollView>
    </PdModal>
  );
};

const useConnectionRequestModal = (): tUseModal<tConnectionRequestModal> => {
  return useModal<tConnectionRequestModal>(ConnectionRequestModal);
};

export default useConnectionRequestModal;

const styles = StyleSheet.create({
  buttonGroup: {
    marginTop: 6 * kStyleConst.kSpacing,
    width: "100%",
  },
  modal: {
    maxWidth: 600,
    padding: 8 * kStyleConst.kSpacing,
    width: "100%",
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  noteTextInput: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    color: kColors.gray500,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    padding: kStyleConst.kSpacing,
  },
  title: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
});
